<script setup lang="ts">
import { computed, h, type FunctionalComponent } from "vue";
import { waBlocks, waPieces, type WaPiece } from "../composables/waFormat";

/**
 * A message's text as WhatsApp shows it: *bold*, _italic_, ~strike~, monospace, `inline code`
 * and links that open in a new tab. With `blocks`, numbered and bulleted lists and quotes are laid
 * out as on WhatsApp Web (a message's bubble); elsewhere (a row's preview) they stay one line of
 * text. Plain text only: nothing is rendered as HTML.
 */
const props = withDefaults(
  defineProps<{
    text: string;
    /** Links as anchors; off inside something already clickable (a conversation's row). */
    links?: boolean;
    blocks?: boolean;
  }>(),
  { links: true, blocks: false },
);

const Pieces: FunctionalComponent<{ pieces: WaPiece[]; links: boolean }> = (
  p,
) =>
  p.pieces.map((x) =>
    x.href && p.links
      ? h(
          "a",
          {
            class: "wa-link",
            href: x.href,
            target: "_blank",
            rel: "noopener noreferrer",
            onClick: (e: Event) => e.stopPropagation(),
          },
          x.text,
        )
      : x.bold || x.italic || x.strike || x.mono || x.code
        ? h(
            "span",
            {
              class: {
                "wa-bold": x.bold,
                "wa-italic": x.italic,
                "wa-strike": x.strike,
                "wa-mono": x.mono,
                "wa-code": x.code,
              },
            },
            x.text,
          )
        : x.text,
  );

const layout = computed(() =>
  (props.blocks
    ? waBlocks(props.text)
    : [{ kind: "text" as const, text: props.text }]
  ).map((b, i, all) =>
    b.kind === "numbered"
      ? {
          kind: b.kind,
          items: b.items.map((x) => ({ n: x.n, pieces: waPieces(x.text) })),
        }
      : b.kind === "bulleted"
        ? {
            kind: b.kind,
            items: b.items.map((x) => ({ n: 0, pieces: waPieces(x) })),
          }
        : {
            kind: b.kind,
            items: [],
            // A list or a quote starts on its own line, so an empty line before it needs one more break.
            pieces: waPieces(
              b.kind === "text" &&
                i < all.length - 1 &&
                (b.text === "" || b.text.endsWith("\n"))
                ? b.text + "\n"
                : b.text,
            ),
          },
  ),
);
</script>

<template>
  <template v-for="(b, i) in layout" :key="i"
    ><ol v-if="b.kind === 'numbered'" class="wa-list">
      <li v-for="(item, j) in b.items" :key="j" :value="item.n">
        <Pieces :pieces="item.pieces" :links="links" />
      </li>
    </ol>
    <ul v-else-if="b.kind === 'bulleted'" class="wa-list">
      <li v-for="(item, j) in b.items" :key="j">
        <Pieces :pieces="item.pieces" :links="links" />
      </li>
    </ul>
    <span v-else-if="b.kind === 'quote'" class="wa-quote"
      ><Pieces :pieces="b.pieces ?? []" :links="links" /></span
    ><Pieces v-else :pieces="b.pieces ?? []" :links="links"
  /></template>
</template>
